/* Green Gorilla Fruits — customer account session probe
 * --------------------------------------------------------------
 * ec-reffer v5.0 (App Proxy + Bearer) フロント側 helper.
 * Shopify App Proxy が Cookie / Set-Cookie を strip する制約に
 * 合わせ、JWT を URL fragment (#session=...) で受け取って
 * localStorage に保存し、後続 API call は Authorization: Bearer
 * <jwt> で認証する。
 *
 * Public API (window.GG_CustomerAccount):
 *   getStatus()     → Promise<{loggedIn, customerId}> (memoized)
 *   getAuthHeader() → {Authorization: 'Bearer ...'} | {}   (sync)
 *   clearSession()  → void   (also resets memoized status)
 *   signOut(url)    → void   (clear + fire-and-forget POST + navigate)
 *
 * Debug logging: enable with `localStorage.setItem('gg_debug', '1')` and
 * reload. Logs include fragment capture, getStatus() resolution, fetch
 * responses (status + body). Prefixed `[gg-referral]` for filtering.
 */
(function () {
  'use strict';

  const SESSION_PATH = '/auth/customer/session';
  const LOGOUT_PATH = '/auth/customer/logout';
  // STORAGE_KEY / HASH_PREFIX must stay in sync with the inline session
  // bootstrap snippet in layout/theme.liquid. Update both sites together.
  const STORAGE_KEY = 'gg_customer_session';
  const HASH_PREFIX = '#session=';
  const DEBUG_KEY = 'gg_debug';
  const LOG_PREFIX = '[gg-referral]';

  function debugEnabled() {
    try {
      return localStorage.getItem(DEBUG_KEY) === '1';
    } catch (e) {
      return false;
    }
  }

  function log() {
    if (!debugEnabled()) return;
    const args = Array.prototype.slice.call(arguments);
    args.unshift(LOG_PREFIX);
    try {
      // eslint-disable-next-line no-console
      console.log.apply(console, args);
    } catch (e) {
      /* ignore */
    }
  }

  function maskToken(jwt) {
    if (!jwt || typeof jwt !== 'string') return '(none)';
    if (jwt.length <= 16) return '(short:' + jwt.length + ')';
    return jwt.slice(0, 8) + '...' + jwt.slice(-8) + ' (len=' + jwt.length + ')';
  }

  function getConfig() {
    return window.GG_REFERRAL_CONFIG && typeof window.GG_REFERRAL_CONFIG === 'object'
      ? window.GG_REFERRAL_CONFIG
      : {};
  }

  function buildUrl(base, path) {
    const normalized = (base || '/apps/referral').replace(/\/+$/, '');
    return normalized + path;
  }

  function readToken() {
    try {
      return localStorage.getItem(STORAGE_KEY);
    } catch (e) {
      return null;
    }
  }

  function writeToken(jwt) {
    try {
      localStorage.setItem(STORAGE_KEY, jwt);
    } catch (e) {
      /* localStorage unavailable — token will live only for this page. */
    }
  }

  function removeToken() {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (e) {
      /* ignore */
    }
  }

  /* Capture JWT from #session=<url_encoded_jwt> emitted by the
   * BFF OAuth callback redirect, then strip the fragment from the
   * URL so it never ends up in browser history / analytics. Runs
   * synchronously at script eval, before any other module observes
   * window.GG_CustomerAccount. */
  function captureFragmentToken() {
    try {
      const hash = window.location.hash || '';
      if (hash.indexOf(HASH_PREFIX) !== 0) {
        log('captureFragmentToken: no #session= fragment', { hash: hash || '(empty)' });
        return;
      }
      const encoded = hash.slice(HASH_PREFIX.length);
      if (!encoded) {
        log('captureFragmentToken: fragment empty after prefix');
        return;
      }
      let jwt;
      try {
        jwt = decodeURIComponent(encoded);
      } catch (e) {
        jwt = encoded;
      }
      if (!jwt) {
        log('captureFragmentToken: decode produced empty value');
        return;
      }
      const segments = jwt.split('.').length;
      // ec-reffer issues a custom 2-segment token (body.signature) — not the
      // 3-segment RFC 7519 JWT. Require at least one `.` so bogus fragments
      // (`#session=garbage` from share links / typos) are dropped, but
      // tolerate both 2- and 3-segment shapes in case the backend migrates.
      if (segments < 2) {
        log('captureFragmentToken: rejected (segments=' + segments + ')', {
          token: maskToken(jwt),
        });
        return;
      }
      writeToken(jwt);
      log('captureFragmentToken: stored JWT', {
        segments: segments,
        token: maskToken(jwt),
      });
      try {
        history.replaceState(null, '', window.location.pathname + window.location.search);
      } catch (e) {
        /* older browsers — leave the URL alone */
      }
    } catch (e) {
      /* defensive — never throw at script eval */
    }
  }

  captureFragmentToken();

  function getAuthHeader() {
    const jwt = readToken();
    return jwt ? { Authorization: 'Bearer ' + jwt } : {};
  }

  let statusPromise = null;

  function clearSession() {
    log('clearSession: removing JWT from localStorage');
    removeToken();
    statusPromise = null;
  }

  function getStatus() {
    if (statusPromise) return statusPromise;
    const jwt = readToken();
    if (!jwt) {
      log('getStatus: no JWT in localStorage → logged-out (no RTT)');
      statusPromise = Promise.resolve({ loggedIn: false, customerId: null });
      return statusPromise;
    }
    const cfg = getConfig();
    const url = buildUrl(cfg.appProxyBase, SESSION_PATH);
    log('getStatus: probing session', { url: url, token: maskToken(jwt) });
    statusPromise = fetch(url, {
      headers: Object.assign({ Accept: 'application/json' }, getAuthHeader()),
    })
      .then((res) => {
        if (res.status === 200) {
          return res
            .json()
            .then((data) => {
              log('getStatus: 200', { body: data });
              return {
                loggedIn: true,
                customerId: data && data.customer_id ? String(data.customer_id) : null,
              };
            })
            .catch((err) => {
              log('getStatus: 200 but JSON parse failed', { error: String(err) });
              return { loggedIn: false, customerId: null };
            });
        }
        if (res.status === 401) {
          return res
            .json()
            .catch(() => ({}))
            .then((body) => {
              log('getStatus: 401', { body: body });
              if (body && body.code === 'invalid_token') clearSession();
              return { loggedIn: false, customerId: null };
            });
        }
        log('getStatus: unexpected status', { status: res.status });
        return { loggedIn: false, customerId: null };
      })
      .catch((err) => {
        log('getStatus: network error', { error: String(err) });
        return { loggedIn: false, customerId: null };
      });
    return statusPromise;
  }

  function signOut(navigateUrl) {
    const cfg = getConfig();
    const url = buildUrl(cfg.appProxyBase, LOGOUT_PATH);
    log('signOut: firing logout POST', { url: url, navigateUrl: navigateUrl });
    // Bearer must be captured before clearSession() because that clears the
    // localStorage entry getAuthHeader() reads from.
    const headers = getAuthHeader();
    try {
      fetch(url, { method: 'POST', headers: headers, keepalive: true }).catch(() => {});
    } catch (e) {
      /* ignore */
    }
    clearSession();
    if (navigateUrl) {
      window.location.href = navigateUrl;
    }
  }

  window.GG_CustomerAccount = {
    getStatus: getStatus,
    getAuthHeader: getAuthHeader,
    clearSession: clearSession,
    signOut: signOut,
  };

  /* Sign out interceptor — intercepts clicks on `<a data-gg-logout>` (the
   * header dropdown sign-out link) so the ec-reffer session is torn down
   * before the browser navigates to routes.account_logout_url. */
  function bindLogoutClicks() {
    document.addEventListener(
      'click',
      function (event) {
        const link =
          event.target && event.target.closest ? event.target.closest('a[data-gg-logout]') : null;
        if (!link) return;
        // Honor modifier-key / non-primary-button intents (open in new tab, etc.)
        if (
          event.defaultPrevented ||
          event.button !== 0 ||
          event.metaKey ||
          event.ctrlKey ||
          event.shiftKey ||
          event.altKey
        ) {
          return;
        }
        event.preventDefault();
        signOut(link.getAttribute('href') || '/');
      },
      false
    );
  }

  // Loaded via `defer`, so DOM is already parsed when this IIFE evaluates;
  // the global click delegation can be bound immediately.
  bindLogoutClicks();
  log('module init complete', { token: maskToken(readToken()) });
})();
